import { create } from 'zustand';

interface VFSStoreState {
  files: Record<string, string>;
  directories: Record<string, string[]>;
  refresh: () => Promise<void>;
  createFile: (path: string, content?: string) => Promise<void>;
  createDirectory: (path: string) => Promise<void>;
  deletePath: (path: string) => Promise<void>;
  renamePath: (oldPath: string, newPath: string) => Promise<void>;
}

export const useVFSStore = create<VFSStoreState>((set) => ({
  files: {},
  directories: {},
  refresh: async () => {
    // Placeholder implementation
    set({ files: {}, directories: {} });
  },
  createFile: async (path: string, content: string = '') => {
    const { files, directories } = useVFSStore.getState();
    
    // Add file to files object
    const newFiles = { ...files, [path]: content };
    
    // Add file to parent directory
    const dirPath = path.substring(0, path.lastIndexOf('/') + 1);
    const newDirectories: Record<string, string[]> = { ...directories };
    if (!newDirectories[dirPath]) {
      newDirectories[dirPath] = [];
    }
    if (!newDirectories[dirPath].includes(path)) {
      newDirectories[dirPath].push(path);
    }
    
    // Update store
    set({ files: newFiles, directories: newDirectories });
    
    // Refresh to ensure UI updates
    await useVFSStore.getState().refresh();
  },
  createDirectory: async (path: string) => {
    const { directories } = useVFSStore.getState();
    
    // Ensure path ends with /
    const dirPath = path.endsWith('/') ? path : path + '/';
    
    // Add directory to directories object
    const newDirectories: Record<string, string[]> = { ...directories, [dirPath]: [] };
    
    // Add directory to parent directory
    const parentPath = dirPath.substring(0, dirPath.lastIndexOf('/', dirPath.length - 2) + 1);
    if (!newDirectories[parentPath]) {
      newDirectories[parentPath] = [];
    }
    if (!newDirectories[parentPath].includes(dirPath)) {
      newDirectories[parentPath].push(dirPath);
    }
    
    // Update store
    set({ directories: newDirectories });
    
    // Refresh to ensure UI updates
    await useVFSStore.getState().refresh();
  },
  deletePath: async (path: string) => {
    const { files, directories } = useVFSStore.getState();
    
    const newFiles = { ...files };
    const newDirectories = { ...directories };
    
    // Remove from files if it's a file
    if (!path.endsWith('/')) {
      delete newFiles[path];
    }
    
    // Remove from parent directory
    const dirPath = path.substring(0, path.lastIndexOf('/') + 1);
    if (newDirectories[dirPath]) {
      newDirectories[dirPath] = newDirectories[dirPath].filter(p => p !== path);
    }
    
    // If it's a directory, remove all its contents recursively
    if (path.endsWith('/')) {
      // Remove all files in this directory
      Object.keys(newFiles).forEach(file => {
        if (file.startsWith(path)) {
          delete newFiles[file];
        }
      });
      
      // Remove all subdirectories and their contents
      Object.keys(newDirectories).forEach(dir => {
        if (dir.startsWith(path)) {
          delete newDirectories[dir];
        }
      });
    }
    
    // Update store
    set({ files: newFiles, directories: newDirectories });
    
    // Refresh to ensure UI updates
    await useVFSStore.getState().refresh();
  },
  renamePath: async (oldPath: string, newPath: string) => {
    const { files, directories } = useVFSStore.getState();
    
    const newFiles = { ...files };
    const newDirectories = { ...directories };
    
    // If it's a file, update the file path
    if (!oldPath.endsWith('/')) {
      const content = newFiles[oldPath];
      delete newFiles[oldPath];
      newFiles[newPath] = content;
    }
    
    // Update in parent directory
    const oldDirPath = oldPath.substring(0, oldPath.lastIndexOf('/') + 1);
    const newDirPath = newPath.substring(0, newPath.lastIndexOf('/') + 1);
    
    if (newDirPath !== oldDirPath) {
      // Moving to different directory
      if (newDirectories[oldDirPath]) {
        newDirectories[oldDirPath] = newDirectories[oldDirPath].filter(p => p !== oldPath);
      }
      if (!newDirectories[newDirPath]) {
        newDirectories[newDirPath] = [];
      }
      newDirectories[newDirPath].push(newPath);
    } else {
      // Same directory, just rename
      if (newDirectories[oldDirPath]) {
        newDirectories[oldDirPath] = newDirectories[oldDirPath].map(p =>
          p === oldPath ? newPath : p
        );
      }
    }
    
    // If it's a directory, update all child paths
    if (oldPath.endsWith('/')) {
      // Update all files in this directory
      Object.keys(newFiles).forEach(file => {
        if (file.startsWith(oldPath)) {
          const relativePath = file.substring(oldPath.length);
          const newFile = newPath + relativePath;
          newFiles[newFile] = newFiles[file];
          delete newFiles[file];
        }
      });
      
      // Update all subdirectories
      Object.keys(newDirectories).forEach(dir => {
        if (dir.startsWith(oldPath)) {
          const relativePath = dir.substring(oldPath.length);
          const newDir = newPath + relativePath;
          newDirectories[newDir] = newDirectories[dir];
          delete newDirectories[dir];
        }
      });
    }
    
    // Update store
    set({ files: newFiles, directories: newDirectories });
    
    // Refresh to ensure UI updates
    await useVFSStore.getState().refresh();
  }
}));